import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Layout from './Layout.jsx';
import LandingPage from './Pages/LandingPage.jsx';
import Sobre from './Pages/Sobre.jsx';
import Dashboard from './Pages/Dashboard.jsx';
import Partnerships from './Pages/Partnerships.jsx';
import Import from './Pages/Import.jsx';
import Reports from './Pages/Reports.jsx';
import OSCReport from './Pages/OSCReport.jsx';
import Login from './Pages/Login.jsx';
import Users from './Pages/Users.jsx';
import EmendasPublicas from './Pages/EmendasPublicas.jsx';
import Audit from './Pages/Audit.jsx';
import RequestPasswordReset from './Pages/RequestPasswordReset.jsx';
import ResetPassword from './Pages/ResetPassword.jsx';
import VerifyEmail from './Pages/VerifyEmail.jsx';
import { AuthService } from './Entities/AuthService';
import { SessionExpiredModal } from './Components/SessionExpiredModal.jsx';
import { useSessionManager } from './hooks/useSessionManager';
import { useGlobalSocket } from './hooks/useGlobalSocket'; // ✅ Socket.IO global
import { Toaster } from './Components/ui/toaster';

// Componente para proteger rotas
function ProtectedRoute({ children }) {
  const isAuthenticated = AuthService.isAuthenticated();
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
}

// Componente para rotas que só admins podem acessar
function AdminRoute({ children }) {
  const isAuthenticated = AuthService.isAuthenticated();
  const canManageUsers = AuthService.canManageUsers();
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  if (!canManageUsers) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
}

// Componente interno que usa hooks do Router
function AppContent() {
  // ✅ SOLUÇÃO UNIFICADA: Hook que gerencia toda a lógica de sessão
  const {
    showExpiredModal,
    expiredReason,
    handleLogout
  } = useSessionManager();

  // ✅ Hook global de Socket.IO (atualização em tempo real para TODAS as páginas)
  useGlobalSocket();

  return (
    <>
      {/* ✅ Modal de sessão expirada (solução unificada) */}
      <SessionExpiredModal
        isOpen={showExpiredModal}
        reason={expiredReason}
        onConfirm={handleLogout}
      />
      
      {/* Toaster para notificações */}
      <Toaster />
      
      <Routes>
        {/* Rota inicial - Landing Page */}
        <Route path="/" element={
          AuthService.isAuthenticated() ? 
            <Navigate to="/dashboard" replace /> : 
            <LandingPage />
        } />
        
        {/* Rotas públicas */}
        <Route path="/login" element={<Login />} />
        <Route path="/sobre" element={<Sobre />} />
        <Route path="/emendas" element={<EmendasPublicas />} />
        <Route path="/request-password-reset" element={<RequestPasswordReset />} />
        <Route path="/reset-password/:token" element={<ResetPassword />} />
        <Route path="/verify-email/:token" element={<VerifyEmail />} />
        
        {/* Rotas protegidas */}
        <Route path="/dashboard" element={
          <ProtectedRoute>
            <Layout currentPageName="Dashboard">
              <Dashboard />
            </Layout>
          </ProtectedRoute>
        } />
        
        <Route path="/partnerships" element={
          <ProtectedRoute>
            <Layout currentPageName="Partnerships">
              <Partnerships />
            </Layout>
          </ProtectedRoute>
        } />
        
        <Route path="/import" element={
          <ProtectedRoute allowedRoles={['super_admin', 'admin', 'user']}>
            <Layout currentPageName="Import">
              <Import />
            </Layout>
          </ProtectedRoute>
        } />
        
        <Route path="/reports" element={
          <ProtectedRoute>
            <Layout currentPageName="Reports">
              <Reports />
            </Layout>
          </ProtectedRoute>
        } />
        
        <Route path="/oscreport" element={
          <ProtectedRoute>
            <Layout currentPageName="OSCReport">
              <OSCReport />
            </Layout>
          </ProtectedRoute>
        } />
        
        {/* Rota de gerenciamento de usuários (apenas para admins) */}
        <Route path="/users" element={
          <AdminRoute>
            <Layout currentPageName="Users">
              <Users />
            </Layout>
          </AdminRoute>
        } />
        
        {/* Rota de auditoria (apenas para admins) */}
        <Route path="/audit" element={
          <AdminRoute>
            <Layout currentPageName="Audit">
              <Audit />
            </Layout>
          </AdminRoute>
        } />
        
        {/* Rota catch-all - redireciona para página inicial */}
        <Route path="*" element={
          AuthService.isAuthenticated() ? 
            <Navigate to="/dashboard" replace /> : 
            <Navigate to="/" replace />
        } />
      </Routes>
    </>
  );
}

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;
